import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { ConfigType } from "dayjs";
import { formatClock } from "@kawkaw/shared";

dayjs.extend(utc);
dayjs.extend(timezone);

const MYT = "Asia/Kuala_Lumpur";

export function isZhLocale(lng: string): boolean {
  return lng === "zh" || lng.startsWith("zh");
}

function d(input: ConfigType) {
  return dayjs(input).tz(MYT);
}

/** Full date + time for headers and detail rows. */
export function formatDateTimeDetail(input: ConfigType, lng: string): string {
  const x = d(input);
  if (isZhLocale(lng)) return `${x.format("YYYY年M月D日")} ${formatClock(dayjs(input).toDate(), lng)}`;
  return `${x.format("D MMM YYYY")} ${formatClock(dayjs(input).toDate(), lng)}`;
}

/** Shorter datetime for timeline sidebars. */
export function formatDateTimeCompact(input: ConfigType, lng: string): string {
  const x = d(input);
  if (isZhLocale(lng)) return `${x.format("M月D日")} ${formatClock(dayjs(input).toDate(), lng)}`;
  return `${x.format("D MMM")} ${formatClock(dayjs(input).toDate(), lng)}`;
}

/** Date only (no time). */
export function formatDateOnly(input: ConfigType, lng: string): string {
  const x = d(input);
  if (isZhLocale(lng)) return x.format("YYYY年M月D日");
  return x.format("D MMM YYYY");
}

/** Order table date only. */
export function formatOrderDate(input: ConfigType, lng: string): string {
  const x = d(input);
  if (isZhLocale(lng)) return x.format("YYYY年M月D日");
  return x.format("D MMM YYYY");
}

/** Order table time only. */
export function formatOrderTime(input: ConfigType, lng: string): string {
  const x = d(input);
  return formatClock(dayjs(input).toDate(), lng);
}

/** Date + time with seconds for audit-style tables. */
export function formatDateTimeWithSeconds(input: ConfigType, lng: string): string {
  const x = d(input);
  if (isZhLocale(lng)) return `${x.format("YYYY年M月D日")} ${formatClock(dayjs(input).toDate(), lng, true)}`;
  return `${x.format("D MMM YYYY")} ${formatClock(dayjs(input).toDate(), lng, true)}`;
}

/** Month + day, useful in compact ranges. */
export function formatMonthDay(input: ConfigType, lng: string): string {
  const x = d(input);
  if (isZhLocale(lng)) return x.format("M月D日");
  return x.format("D MMM");
}

/** Sortable Malaysia-time stamp for CSV exports ("2026-10-24 21:07:59") — never UTC, so it matches what staff see on screen. */
export function formatMytStamp(input: ConfigType): string {
  return d(input).format("YYYY-MM-DD HH:mm:ss");
}
